import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Checkbox, Divider, MenuItem, MenuList } from "@mui/material";
import { DropDown, icon, IconComponent } from "../../../../../../../common/components";
import SmartReviewContext from "../../../../../context/smart-review-context";
import { setPartners, setShowVersioning } from "../../../../../slice/document/document-slice";
import {
	environmentSwitch,
	hasCompanyPermission,
	hasPermission,
	isSegFeatureEnabled,
	Permissions,
	SegFlags,
	translate,
} from "../../../../../../../common/providers";
import styles from "./OptionsDropDown.module.css";
import { downloadFile, isNonEmptyObject } from "../../../../../../../common/utils";
import documentService from "../../../../../../../api/services/document/document-service";
import { useApi } from "../../../../../../../common/hooks";
import { NavUtils } from "../../../../../../../navigation";
import { DOCUMENT_STATUSES } from "../../../../../constants/constants";

const OptionsDropDown = React.forwardRef(({ onClose = null, open }, ref) => {
	const { call: onDownloadDocument } = useApi(documentService.downloadDocument);
	const { call: getDocumentDetails } = useApi(documentService.getInformation);
	const partners = useSelector(({ srDocument }) => srDocument.partners);
	const showVersioning = useSelector(({ srDocument }) => srDocument.showVersioning);
	const docId = useSelector(({ srDocument }) => srDocument.documentId);
	const projectId = useSelector(({ context }) => context?.project?.id);
	const [folderId, setFolderId] = useState("");
	const [docDetailes, setDocDetailes] = useState({});
	const isArchived = useSelector(({ srDocument }) => srDocument.status === DOCUMENT_STATUSES.ARCHIVED);
	const dispatch = useDispatch();

	useEffect(() => {
		const savedPreferences = SmartReviewContext.getContext()?.preferences;
		if (savedPreferences?.versioning) {
			dispatch(setShowVersioning(savedPreferences.versioning));
		}
	}, [dispatch]);
	const hasActionPermission = environmentSwitch({
		main:
			hasPermission([Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER]) &&
			hasCompanyPermission([Permissions.PROJECT_LEADER]),
		veolia: true,
	});
	const handleCheckPartners = (partner) => {
		SmartReviewContext.setPartners({
			partners: partners.map((x) =>
				x.id === partner.id ? { id: x.id, display: !x.display } : { id: x.id, display: x.display }
			),
		});
		dispatch(setPartners(partners.map((x) => (x.id === partner.id ? { ...x, display: !x.display } : x))));
	};
	const handleCheckVersioning = () => {
		SmartReviewContext.setPreferences({
			preferences: {
				versioning: !showVersioning,
			},
		});
		dispatch(setShowVersioning(!showVersioning));
	};
	const handleDownload = () => {
		onDownloadDocument({ docId })
			.then(({ data, filename }) => downloadFile({ data, filename, filetype: "zip" }))
			.catch((err) => console.error(err));
	};
	const handleRetrieveHierarchy = () => {
		if (!folderId) {
			getDocumentDetails({ docId })
				.then((data) => {
					if (isNonEmptyObject(data)) {
						const dirId = (!data.directoryId && "root") || data.directoryId;
						setDocDetailes(data);
						setFolderId(dirId);
					}
				})
				.catch(console.error);
		}
	};
	const handleOpenDocumentCenter = () => {
		if (folderId) {
			window.open(
				NavUtils.goToDocumentCenter(projectId, folderId, docId, true, docDetailes.fileType, docDetailes.status)
			);
		}
	};
	return (
		<DropDown ref={ref} open={open} onClose={onClose}>
			<MenuList className={styles.dropDownContainer}>
				{isArchived && (
					<>
						{isSegFeatureEnabled(SegFlags.VERSIONS) && (
							<MenuItem className={styles.menuItem} onClick={handleCheckVersioning}>
								<Checkbox checked={showVersioning} className={styles.menuItem__icon} size="small" />
								<span className={styles.menuItem__text}>
									{translate("smart-review.options-drop-down.show-versioning")}
								</span>
							</MenuItem>
						)}
						{Array.isArray(partners) &&
							partners.length > 0 &&
							partners.map((partner) => (
								<MenuItem
									key={partner.id}
									className={styles.menuItem}
									onClick={() => handleCheckPartners(partner)}
								>
									<Checkbox
										checked={partner.display}
										className={styles.menuItem__icon}
										size="small"
									/>
									<span className={styles.menuItem__text}>{partner.name}</span>
								</MenuItem>
							))}
						<Divider className={styles.divider} />
					</>
				)}
				{hasActionPermission && (
					<MenuItem className={styles.menuItem} onClick={handleDownload}>
						<IconComponent
							className={styles.menuItem__icon}
							color="var(--grey-600)"
							icon={icon.faDownload}
							size="xs"
						/>
						<span className={styles.menuItem__text}>
							{translate("smart-review.options-drop-down.download-document")}
						</span>
					</MenuItem>
				)}
				<MenuItem
					className={styles.menuItem}
					onClick={handleOpenDocumentCenter}
					onMouseOver={handleRetrieveHierarchy}
				>
					<IconComponent
						className={styles.menuItem__icon}
						color="var(--grey-600)"
						icon={icon.faFileLines}
						size="xs"
					/>
					<span className={styles.menuItem__text}>
						{translate("smart-review.options-drop-down.navigate-to-document-center")}
					</span>
				</MenuItem>
			</MenuList>
		</DropDown>
	);
});

export default OptionsDropDown;
